/**
 * Collapsible local graph under the note editor: the note and its neighbours.
 * Canvas + d3-force load lazily once the section is open; expand opens the
 * full-page graph centered on this note.
 */
import { EDITOR_GUTTER_PADDING_LEFT } from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import { buildLocalGraph } from '@bangle.io/service-core';
import { Button, cn } from '@bangle.io/ui-components';
import type { WsFilePath } from '@bangle.io/ws-path';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';
import React, { lazy, Suspense, useMemo } from 'react';
import { buildGraphView, isPageNode, type ViewNode } from './graphModel';
import { useGraphSettings } from './graphSettings';

const GraphCanvas = lazy(() =>
  import('./GraphCanvas').then((m) => ({ default: m.GraphCanvas })),
);

export function LocalGraphSection({
  currentWsPath,
}: {
  currentWsPath: WsFilePath;
}) {
  const coreServices = useCoreServices();
  const noteLinkGraph = useAtomValue(
    coreServices.workspaceState.$noteLinkGraph,
  );
  const [collapsed, setCollapsed] = useAtom(
    coreServices.workbenchState.$localGraphCollapsed,
  );
  const wsName = currentWsPath.wsName;
  const [settings, update] = useGraphSettings(wsName);

  const data = useMemo(() => {
    if (collapsed || noteLinkGraph.status === 'idle') {
      return null;
    }
    if (noteLinkGraph.status === 'error') {
      return null;
    }
    return buildLocalGraph(noteLinkGraph.graph, currentWsPath.wsPath, {
      depth: settings.depth,
      direction: settings.direction,
    });
  }, [
    collapsed,
    currentWsPath.wsPath,
    noteLinkGraph,
    settings.depth,
    settings.direction,
  ]);

  const groupsKey = JSON.stringify(settings.groups);
  const view = useMemo(
    () =>
      data
        ? buildGraphView(data, settings, data.center, {
            rootFolder: t.app.graph.groups.rootFolder,
          })
        : null,
    // Only what changes the view (not force sliders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      settings.search,
      settings.showOrphans,
      settings.colorBy,
      groupsKey,
      settings.sizeByDegree,
      settings.nodeSize,
    ],
  );

  const open = (node: ViewNode) => {
    if (!isPageNode(node) || node.id === currentWsPath.wsPath) {
      return;
    }
    coreServices.navigation.goWsFile(node.id);
  };

  const neighbours = view
    ? view.nodes.filter(
        (n) => isPageNode(n) && n.id !== currentWsPath.wsPath,
      ).length
    : 0;

  const isLoading =
    !collapsed &&
    (noteLinkGraph.status === 'loading' || noteLinkGraph.status === 'idle');
  const hasError = !collapsed && noteLinkGraph.status === 'error';

  return (
    <section
      aria-labelledby="local-graph-heading"
      data-testid="local-graph"
      className={cn(
        'border-border border-t py-4 pr-4 md:pr-6',
        EDITOR_GUTTER_PADDING_LEFT,
      )}
    >
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-expanded={!collapsed}
            aria-controls="local-graph-content"
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </Button>
          <h2
            id="local-graph-heading"
            className="min-w-0 flex-1 truncate font-medium text-sm"
          >
            {t.app.graph.local.title}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={t.app.graph.local.expand}
            title={t.app.graph.local.expand}
            onClick={() => {
              coreServices.navigation.goGraph({
                wsName,
                centerWsPath: currentWsPath.wsPath,
              });
            }}
          >
            <Maximize2 className="size-4" />
          </Button>
        </div>

        {!collapsed && (
          <div
            id="local-graph-content"
            className="flex flex-col gap-2"
          >
            <div
              className="flex items-center gap-0.5"
              role="group"
              aria-label={t.app.graph.local.depth}
            >
              <span
                className="mr-1 text-muted-foreground text-xs"
                aria-hidden="true"
              >
                {t.app.graph.local.depth}
              </span>
              {[1, 2, 3].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => update({ depth: d })}
                  aria-pressed={settings.depth === d}
                  aria-label={
                    d === 1
                      ? t.app.graph.local.depthHint1
                      : d === 2
                        ? t.app.graph.local.depthHint2
                        : t.app.graph.local.depthHint3
                  }
                  title={
                    d === 1
                      ? t.app.graph.local.depthHint1
                      : d === 2
                        ? t.app.graph.local.depthHint2
                        : t.app.graph.local.depthHint3
                  }
                  className={cn(
                    'h-6 w-6 rounded text-xs tabular-nums',
                    settings.depth === d
                      ? 'bg-muted font-semibold text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {d}
                </button>
              ))}
            </div>

            <div className="relative h-56 overflow-hidden rounded-md border border-border bg-muted/30">
              {view && view.nodes.length > 0 ? (
                <Suspense fallback={null}>
                  <GraphCanvas
                    key={currentWsPath.wsPath}
                    nodes={view.nodes}
                    links={view.links}
                    settings={settings}
                    onOpen={open}
                    onSelect={(n) => n && open(n)}
                    compact
                    ariaLabel={t.app.graph.aria({
                      nodes: view.nodes.length,
                      links: view.links.length,
                    })}
                  />
                </Suspense>
              ) : (
                <div className="flex h-full items-center justify-center px-3 text-center text-muted-foreground text-xs">
                  {hasError
                    ? t.app.graph.error
                    : data
                      ? t.app.graph.local.none
                      : isLoading
                        ? t.app.graph.loading
                        : t.app.graph.local.none}
                </div>
              )}
              {view && neighbours === 0 && view.nodes.length > 0 && (
                <div className="pointer-events-none absolute inset-x-0 bottom-1 px-3 text-center text-muted-foreground text-xs">
                  {t.app.graph.local.none}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
